import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import api from '../utils/api';
import WeddingVenues from './WeddingVenues';
import WeddingStyleTheme from './WeddingStyleTheme';
import MusicSelections from './MusicSelections';
import BasicInformation from './PartnerDetails/BasicInformation';
import WeddingCulturalReligious from './WeddingCulturalReligious';
import WeddingExtraDetails from './WeddingExtraDetails';

import {
  Users,
  MusicNote,
  Copy,
  GlobeHemisphereEast,
  CalendarHeart,
  Barn,
  DiscoBall,
} from '@phosphor-icons/react';

function WeddingDetailsDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [_weddingDetails, setWeddingDetails] = useState(null);
  const [_wedding, setWedding] = useState(null);
  const [accessCode, setAccessCode] = useState('');
  const [copied, setCopied] = useState(false);
  const theme = useTheme();

  const sectionGroups = useMemo(() => [
    {
      label: 'Couple & Details',
      sections: [
        { key: 'couple-information', title: 'Couple Information', description: 'Names, pronouns and core contact details', icon: Users, bgColor: 'rgba(233, 30, 99, 0.1)' },
        { key: 'cultural', title: 'Cultural & Religious', description: 'Ceremony and tradition preferences', icon: GlobeHemisphereEast, bgColor: 'rgba(103, 58, 183, 0.1)' },
        { key: 'extras', title: 'Extra Details', description: 'Proposal date and surname plans', icon: CalendarHeart, bgColor: 'rgba(96, 125, 139, 0.1)' },
      ],
    },
    {
      label: 'Venue & Style',
      sections: [
        { key: 'venues', title: 'Venues & Dates', description: 'Manage ceremony and reception', icon: Barn, bgColor: 'rgba(0, 150, 136, 0.1)' },
        { key: 'style-theme', title: 'Style & Theme', description: 'Define your wedding aesthetic', icon: DiscoBall, bgColor: 'rgba(255, 152, 0, 0.1)' },
        { key: 'music', title: 'Music', description: 'Choose your ceremony and reception music', icon: MusicNote, bgColor: 'rgba(63, 81, 181, 0.1)' },
      ],
    },
  ], []);

  const sections = useMemo(() => sectionGroups.flatMap(g => g.sections), [sectionGroups]);

  const activeSection = useMemo(() => {
    const requested = searchParams.get('section');
    const matchedSection = sections.find((s) => s.key === requested);
    return matchedSection ? matchedSection.key : 'couple-information';
  }, [searchParams, sections]);

  useEffect(() => {
    if (!searchParams.get('section') && sections.length > 0) {
      setSearchParams({ section: 'couple-information' }, { replace: true });
    }
  }, [searchParams, sections.length, setSearchParams]);

  const handleSectionChange = (sectionKey) => {
    setSearchParams({ section: sectionKey });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const [weddingRes, detailsRes] = await Promise.all([
          api.get('/api/wedding'),
          api.get('/api/wedding-details')
        ]);

        setWedding(weddingRes.data);
        setWeddingDetails(detailsRes.data);

        // Fetch access code separately
        try {
          const accessCodeRes = await api.get('/api/wedding/access-code');
          setAccessCode(accessCodeRes.data?.accessCode || '');
        } catch (accessError) {
          // If access code fetch fails, try to get it from wedding data
          setAccessCode(weddingRes.data?.accessCode || '');
        }
      } catch (error) {
        setError('Failed to fetch wedding information');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(accessCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {}
  };



  const renderPlanningSection = () => {
    switch (activeSection) {
      case 'couple-information':
        return <BasicInformation title="Couple Information" embedded />;
      case 'venues':
        return <WeddingVenues embedded highlightField={searchParams.get('highlight')} />;
      case 'style-theme':
        return <WeddingStyleTheme embedded />;
      case 'cultural':
        return <WeddingCulturalReligious embedded />;
      case 'extras':
        return <WeddingExtraDetails embedded />;
      case 'music':
        return <MusicSelections embedded />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
        {/* Left Sidebar + Wedding Code */}
        <Box
          sx={{
            width: { xs: '100%', md: 280 },
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            position: { md: 'sticky' },
            top: { md: 20 },
            height: 'fit-content',
          }}
        >
          <Card
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.5)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ px: 2, pt: 3, pb: 2 }}>
                <Typography variant="h5" component="h1" sx={{ fontWeight: 500 }}>
                  Wedding Planning
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Personalise your wedding experience
                </Typography>
              </Box>

              {/* Mobile dropdown */}
            <Box sx={{ display: { xs: 'block', md: 'none' }, px: 2, pb: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Section</InputLabel>
                <Select
                  value={activeSection}
                  label="Section"
                  onChange={(e) => handleSectionChange(e.target.value)}
                >
                  {sections.map((s) => (
                    <MenuItem key={s.key} value={s.key}>
                      {s.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Desktop grouped list */}
            <List sx={{ p: 0, display: { xs: 'none', md: 'block' } }}>
              {sectionGroups.map((group) => (
                <Box key={group.label}>
                  <Typography
                    variant="overline"
                    sx={{ px: 2, pt: 2, pb: 1, display: 'block', color: 'text.secondary', fontSize: '0.7rem' }}
                  >
                    {group.label}
                  </Typography>
                  {group.sections.map((section) => {
                    const isActive = activeSection === section.key;
                    const Icon = section.icon;
                    return (
                      <ListItemButton
                        key={section.key}
                        onClick={() => handleSectionChange(section.key)}
                        selected={isActive}
                        sx={{
                          px: 2,
                          py: 1.25,
                          '&.Mui-selected': {
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.12) },
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Icon size={20} weight="regular" color={isActive ? theme.palette.primary.main : undefined} />
                        </ListItemIcon>
                        <ListItemText
                          primary={section.title}
                          primaryTypographyProps={{
                            variant: 'body2',
                            fontWeight: isActive ? 600 : 400,
                            color: isActive ? 'primary.main' : 'text.primary',
                          }}
                        />
                      </ListItemButton>
                    );
                  })}
                </Box>
              ))}
            </List>
          </CardContent>
        </Card>

          {accessCode && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: '#fff',
              }}
            >
              <Typography variant="caption" color="text.secondary" display="block">
                Your Wedding Code
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                  {accessCode}
                </Typography>
                <Tooltip title={copied ? 'Copied!' : 'Copy code'}>
                  <IconButton size="small" onClick={handleCopyCode} sx={{ p: 0.25 }}>
                    <Copy size={16} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          )}
        </Box>

        {/* Right content area */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Card sx={{ flex: 1, overflow: 'visible' }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              {renderPlanningSection()}
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
}

export default WeddingDetailsDashboard; 